<template>
  <div class="analysis-manager">
    <!-- Debug info -->
    <div class="debug-info" style="margin-bottom: 20px; padding: 10px; background: #f5f5f5;">
      <p>Number of documents: {{ documents.length }}</p>
      <p>Number of workflows: {{ workflows.length }}</p>
      <p>Selected documents: {{ selectedDocuments.length }}</p>
      <p>Active session: {{ activeSession ? 'Yes' : 'No' }}</p>
      
      <!-- Add additional debug info for analyses -->
      <div v-if="currentSession && currentSession.analyses" style="margin-top: 10px;">
        <p><strong>Current Session Analyses:</strong></p>
        <ul>
          <li v-for="(analysis, index) in currentSession.analyses" :key="analysis.id">
            Analysis #{{ index + 1 }} (ID: {{ analysis.id }}): 
            Document ID: {{ analysis.document_id || 'Missing' }} |
            Status: {{ analysis.status }}
          </li>
        </ul>
      </div>
      
      <!-- Add document list for debugging -->
      <div style="margin-top: 10px; max-height: 100px; overflow-y: auto;">
        <p><strong>Available Documents:</strong></p>
        <ul>
          <li v-for="doc in documents" :key="doc.id">
            ID: {{ doc.id }} ({{ typeof doc.id }}) | Name: {{ doc.name }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Create New Session -->
    <div class="session-creator" v-if="!currentSession">
      <h3>Create Analysis Session</h3>
      <div class="document-selector">
        <h4>Select Documents to Analyze</h4>
        <div v-for="doc in documents" :key="doc.id" class="document-option">
          <input type="checkbox" v-model="selectedDocuments" :value="doc.id">
          <span>{{ doc.name }}</span>
        </div>
      </div>
      
      <div class="workflow-selector" v-if="selectedDocuments.length">
        <h4>Select Workflows</h4>
        <div v-for="doc in selectedDocuments" :key="doc">
          <h5>{{ doc ? getDocumentName(doc) : 'Unknown Document' }}</h5>
          <select v-model="documentWorkflows[doc]">
            <option value="">Select Workflow</option>
            <option v-for="wf in workflows" :key="wf.id" :value="wf.id">
              {{ wf.name }}
            </option>
          </select>
        </div>
      </div>

      <button @click="createSession" :disabled="!canCreateSession">
        Start Analysis Session
      </button>
    </div>

    <!-- Active Session View -->
    <div class="active-session" v-else>
      <div class="session-header">
        <h3>{{ currentSession.name }}</h3>
        <span class="session-status">{{ currentSession.status }}</span>
      </div>

      <!-- Analysis Results -->
      <div class="analysis-results">
        <div v-for="analysis in currentSession.analyses" :key="analysis.id" 
             class="analysis-card">
          <div class="analysis-header">
            <h4 v-if="analysis.document_id">{{ getDocumentName(analysis.document_id) }}</h4>
            <h4 v-else>Analysis #{{ analysis.id }}</h4>
            <div class="analysis-meta">
              <span :class="['status-badge', getStatusClass(analysis.status)]">
                {{ analysis.status }}
              </span>
              <span v-if="analysis.started_at" class="analysis-date">
                Started: {{ formatDate(analysis.started_at) }}
              </span>
              <span v-if="analysis.completed_at" class="analysis-date">
                Completed: {{ formatDate(analysis.completed_at) }}
              </span>
              <span v-if="analysis.processing_duration" class="analysis-duration">
                Duration: {{ formatDuration(analysis.processing_duration) }}
              </span>
            </div>
          </div>
          
          <div v-if="analysis.status === 'PROCESSING' || analysis.status === 'PENDING'" class="processing-indicator">
            <div class="spinner"></div>
            <span>Processing document analysis...</span>
          </div>
          
          <div v-if="analysis.status === 'FAILED'" class="error-message">
            <i class="fas fa-exclamation-circle"></i>
            <span>Analysis failed. Please try again or contact support.</span>
          </div>
          
          <div v-if="analysis.status === 'TIMEOUT'" class="timeout-message">
            <i class="fas fa-clock"></i>
            <span>Analysis timed out. The document may be too complex or the system may be experiencing high load.</span>
          </div>
          
          <!-- Debug info for results -->
          <div class="debug-results" style="margin: 10px 0; padding: 5px; background: #f0f0f0; border: 1px dashed #999;">
            <p><strong>Results Debug:</strong></p>
            <p>Analysis ID: {{ analysis.id }}</p>
            <p>Document ID: {{ analysis.document_id || 'Missing' }} 
              <span v-if="!analysis.document_id" style="color: red;">(ID Missing)</span>
            </p>
            <p v-if="analysis.document_id">Document in List: {{ documents.some(d => d.id == analysis.document_id) ? 'Yes' : 'No' }}</p>
            <p>Status: {{ analysis.status }}</p>
            <p>Has results: {{ !!analysis.results }}</p>
            <p>Results type: {{ analysis.results ? typeof analysis.results : 'none' }}</p>
            <p v-if="analysis.results">Results keys: 
              <span v-if="typeof analysis.results === 'object'">{{ Object.keys(analysis.results).join(', ') }}</span>
              <span v-else>N/A (not an object)</span>
            </p>
          </div>
          
          <div v-if="analysis.results" class="results-viewer">
            <h5>Analysis Results</h5>
            
            <!-- Display results by Step -->
            <div v-if="typeof analysis.results === 'object'" class="step-based-results">
              <div v-for="(stepResult, stepId) in analysis.results" :key="stepId" class="step-result-card">
                <h6>Step {{ stepId }} ({{ stepResult.step_type || 'Unknown Type' }})</h6>
                <div v-if="stepResult.response">
                  <strong>Response:</strong>
                  <!-- Try to parse and pretty-print if response is a JSON string -->
                  <pre v-if="isJsonString(stepResult.response)">{{ prettyPrintJson(stepResult.response) }}</pre>
                  <!-- Otherwise, display as plain text -->
                  <pre v-else>{{ stepResult.response }}</pre>
                </div>
                <div v-else>
                  <p><em>No response recorded for this step.</em></p>
                </div>
              </div>
            </div>
            <!-- Fallback for non-object results -->
            <pre v-else class="raw-results">{{ analysis.results }}</pre>
          </div>

          <!-- Client Feedback Section -->
          <div class="feedback-section" v-if="analysis.feedback && analysis.feedback.length">
            <h5>Client Feedback</h5>
            <div v-for="feedback in analysis.feedback" :key="feedback.id"
                 class="feedback-item">
              <span class="feedback-type">{{ feedback.feedback_type }}</span>
              <p>{{ feedback.content }}</p>
              <div v-if="feedback.resolution" class="resolution">
                {{ feedback.resolution }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Session Actions -->
      <div class="session-actions">
        <button @click="shareWithClient" 
                v-if="currentSession.status === 'DRAFT'">
          Share with Client
        </button>
        <button @click="completeSession" 
                v-if="currentSession.status === 'IN_REVIEW'">
          Complete Session
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/api';
import { errorHandlingMixin } from '@/mixins/errorHandling';

export default {
  name: 'AnalysisSessionManager',
  mixins: [errorHandlingMixin],
  
  props: {
    projectId: {
      type: [String, Number],
      required: true
    },
    documents: {
      type: Array,
      required: true
    },
    workflows: {
      type: Array,
      required: true
    },
    activeSession: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      selectedDocuments: [],
      documentWorkflows: {},
      localActiveSession: null,
      socket: null,
      analysisProgress: {}
    };
  },

  computed: {
    canCreateSession() {
      return this.selectedDocuments.length > 0 &&
             this.selectedDocuments.every(docId => this.documentWorkflows[docId]);
    },
    
    // Use the prop if provided, otherwise use local state
    currentSession() {
      return this.activeSession || this.localActiveSession;
    }
  },

  methods: {
    async createSession() {
      let response;
      try {
        const requestData = {
          project: parseInt(this.projectId),
          name: `Analysis Session ${new Date().toLocaleDateString()}`,
          status: 'DRAFT',
          documents: this.selectedDocuments.map(docId => ({
            document_id: docId,
            workflow_id: this.documentWorkflows[docId]
          }))
        };

        console.log('Creating session with data:', requestData);

        response = await api.post(`/api/projects/${this.projectId}/analysis-sessions/`, requestData);
        console.log('API Response received in createSession:', response); // Log raw response

        // Check if response.data looks valid, otherwise check response itself
        let sessionData = null;
        if (response && response.data && typeof response.data === 'object' && response.data.id) {
            sessionData = response.data;
            console.log('Validated Session data from response.data:', sessionData); 
        } else if (response && typeof response === 'object' && response.id) {
            sessionData = response; // Assume response *is* the data
            console.log('Validated Session data directly from response:', sessionData); 
        } else {
            // If neither structure is valid
            console.error('Invalid API Response Structure. Expected session data in response or response.data:', response);
            throw new Error('Invalid response format: missing valid session data with ID');
        }
        
        // Update local state
        this.localActiveSession = sessionData;
        
        // Emit event with session data
        this.$emit('session-created', sessionData);
        
        // Reset form and show success message
        this.resetForm();
        if (this.toast) {
          this.toast.success(response.message || 'Analysis session created successfully');
        }

      } catch (error) {
        console.error('Session creation error:', error);
        console.error('Last response:', response); // Log the last response for debugging
        this.handleError(error);
      }
    },

    async shareWithClient() {
      if (!this.currentSession) return;

      try {
        const response = await api.post(
          `/api/projects/${this.projectId}/analysis-sessions/${this.currentSession.id}/share`
        );
        
        if (response && response.success && response.session) {
          this.localActiveSession = response.session;
          this.$emit('session-updated', response.session);
          this.toast.success('Session shared with client');
        } else {
          throw new Error(response?.error || 'Invalid response from server');
        }
      } catch (error) {
        this.handleError(error, 'Error sharing session');
      }
    },

    async completeSession() {
      if (!this.currentSession) return;

      try {
        const response = await api.post(
          `/api/projects/${this.projectId}/analysis-sessions/${this.currentSession.id}/complete`
        );
        
        if (response && response.success && response.session) {
          this.localActiveSession = response.session;
          this.$emit('session-updated', response.session);
          this.toast.success('Session completed successfully');
        } else {
          throw new Error(response?.error || 'Invalid response from server');
        }
      } catch (error) {
        this.handleError(error, 'Error completing session');
      }
    },

    resetForm() {
      this.selectedDocuments = [];
      this.documentWorkflows = {};
    },

    getDocumentName(docId) {
      // Handle empty document ID
      if (!docId && docId !== 0) {
        console.warn('Received empty document ID');
        return 'Unknown Document';
      }

      // If documents array is empty or not available
      if (!this.documents || !this.documents.length) {
        console.warn('No documents available for lookup');
        return `Document ${docId}`;
      }

      // Convert to number if it's a string ID since API might return different types
      const numericId = typeof docId === 'string' ? parseInt(docId, 10) : docId;
      
      // Try exact match first
      let doc = this.documents.find(d => d.id === docId);
      
      // If not found, try with numeric conversion
      if (!doc && !isNaN(numericId)) {
        doc = this.documents.find(d => d.id === numericId);
      }
      
      // Additional check with string conversion
      if (!doc) {
        const stringId = docId.toString();
        doc = this.documents.find(d => d.id.toString() === stringId);
      }
      
      // If still not found, log available documents for debugging
      if (!doc) {
        console.warn(`Document not found for ID: ${docId} (type: ${typeof docId})`);
        console.log('Available documents:', this.documents.map(d => ({id: d.id, type: typeof d.id, name: d.name})));
        
        // Return a better fallback value
        return `Document ${docId}`;
      }
      
      return doc.name;
    },

    initWebSocket(sessionId) {
      const wsScheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const wsHost = window.location.host; // Use current host (works in both dev and production)
      const wsUrl = `${wsScheme}://${wsHost}/ws/analysis/${sessionId}/`;
      console.log('Connecting to WebSocket:', wsUrl);
      
      this.socket = new WebSocket(wsUrl);
      
      this.socket.onopen = () => {
        console.log('WebSocket connected for session:', sessionId);
      };
      
      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      this.socket.onmessage = (event) => {
        console.log('WebSocket message received:', event.data);
        const data = JSON.parse(event.data);
        if (data.type === 'analysis_progress') {
          this.handleProgressUpdate(data);
        }
      };

      this.socket.onclose = () => {
        console.log('WebSocket disconnected for session:', sessionId);
      };
    },

    handleProgressUpdate(data) {
      console.log('Handling progress update:', data);
      // Update the session status
      const session = this.sessions.find(s => 
        s.analyses.some(a => a.id === data.analysis_id)
      );
      if (session) {
        const analysis = session.analyses.find(a => a.id === data.analysis_id);
        if (analysis) {
          analysis.status = data.status;
          analysis.results = data.results;
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleString();
    },
    
    formatDuration(seconds) {
      if (!seconds) return '';
      if (seconds < 60) return `${seconds} seconds`;
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes} min ${remainingSeconds} sec`;
    },
    
    formatKey(key) {
      return key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
    },
    
    getStatusClass(status) {
      switch(status) {
        case 'COMPLETED': return 'status-completed';
        case 'PROCESSING': return 'status-processing';
        case 'PENDING': return 'status-pending';
        case 'FAILED': return 'status-failed';
        case 'TIMEOUT': return 'status-timeout';
        default: return '';
      }
    },

    failedFindings(results) {
      return results.findings.filter(f => f.severity === 'failed');
    },

    passedFindings(results) {
      return results.findings.filter(f => f.severity === 'passed');
    },

    isJsonString(str) {
      try {
        JSON.parse(str);
        return true;
      } catch (e) {
        return false;
      }
    },

    prettyPrintJson(json) {
      return JSON.stringify(JSON.parse(json), null, 2);
    }
  },

  mounted() {
    console.log('AnalysisSessionManager mounted', {
      projectId: this.projectId,
      documents: this.documents,
      workflows: this.workflows
    });

    // Log details about active session if present
    if (this.activeSession) {
      console.log('Active session data:', this.activeSession);
      
      if (this.activeSession.analyses && this.activeSession.analyses.length > 0) {
        console.log('Analysis document IDs:', this.activeSession.analyses.map(a => ({
          analysisId: a.id,
          documentId: a.document_id,
          documentIdType: typeof a.document_id
        })));

        // Log detailed information about each analysis
        console.log('Detailed analyses:');
        this.activeSession.analyses.forEach((analysis, index) => {
          console.log(`Analysis #${index + 1}:`, {
            id: analysis.id,
            document_id: analysis.document_id,
            has_document_id: !!analysis.document_id,
            status: analysis.status,
            has_results: !!analysis.results,
            results_type: analysis.results ? typeof analysis.results : 'none',
            results_preview: analysis.results ? 
              (typeof analysis.results === 'object' ? 
                'Object with keys: ' + Object.keys(analysis.results).join(', ') : 
                (analysis.results.length > 100 ? 
                  analysis.results.substring(0, 100) + '...' : 
                  analysis.results)
              ) : 'No results',
            all_keys: Object.keys(analysis)
          });
          
          // If there are results, log the full structure for one analysis to understand the format
          if (analysis.results && typeof analysis.results === 'object') {
            console.log('Full results structure for analysis', analysis.id);
            
            // Log each step result separately to understand the structure
            Object.keys(analysis.results).forEach(stepId => {
              console.log(`Step ${stepId} result:`, {
                step_type: analysis.results[stepId].step_type,
                sample_content: JSON.stringify(analysis.results[stepId]).substring(0, 200) + '...'
              });
            });
          }
        });
      } else {
        console.log('No analyses found in active session');
      }
      
      console.log('Available document IDs:', this.documents.map(d => ({
        id: d.id,
        idType: typeof d.id,
        name: d.name
      })));
    }
  },

  watch: {
    documents: {
      handler(newDocs) {
        console.log('Documents updated:', newDocs);
      },
      deep: true
    },
    workflows: {
      handler(newWorkflows) {
        console.log('Workflows updated:', newWorkflows);
      },
      deep: true
    },
    currentSession(newSession) {
      if (newSession && newSession.status === 'PROCESSING') {
        this.initWebSocket(newSession.id);
      }
    }
  }
};
</script>

<style scoped>
.analysis-manager {
  margin-top: 2rem;
  padding: 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
}

.session-creator {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.document-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.5rem 0;
}

.workflow-selector {
  margin-top: 1rem;
}

.analysis-card {
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 1rem;
  margin: 1rem 0;
}

.analysis-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.analysis-meta {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.status-badge {
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 500;
}

.status-completed {
  background-color: #c6f6d5;
  color: #22543d;
}

.status-processing, .status-pending {
  background-color: #bee3f8;
  color: #2b6cb0;
}

.status-failed {
  background-color: #fed7d7;
  color: #c53030;
}

.status-timeout {
  background-color: #feebc8;
  color: #c05621;
}

.analysis-date {
  font-size: 0.8rem;
  color: #666;
}

.analysis-duration {
  font-size: 0.8rem;
  color: #666;
}

.processing-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 1rem 0;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #007bff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  animation: spin 0.75s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-message {
  color: #dc3545;
  margin: 1rem 0;
}

.timeout-message {
  color: #ffc107;
  margin: 1rem 0;
}

.results-viewer {
  margin-top: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 1rem;
  background-color: #f8fafc;
}

.results-viewer h5 {
  margin-top: 0;
  margin-bottom: 0.5rem;
  color: #2d3748;
  font-size: 1rem;
}

.step-based-results {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.step-result-card {
  padding: 0.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
}

.raw-results {
  max-height: 200px;
  overflow-y: auto;
  background-color: #edf2f7;
  padding: 0.5rem;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 0.8rem;
  white-space: pre-wrap;
  margin: 0;
}

.feedback-item {
  margin: 1rem 0;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.feedback-type {
  font-weight: bold;
  color: #666;
}

.resolution {
  margin-top: 0.5rem;
  padding: 0.5rem;
  background: #e9ecef;
  border-radius: 4px;
}

button {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  background: #007bff;
  color: white;
  cursor: pointer;
}

button:disabled {
  background: #ccc;
  cursor: not-allowed;
}
</style> 